import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { App } from "./App";

declare global {
  interface Window {
    __SENTRY_DSN__?: string;
  }
}

// Init Sentry only when the server-injected DSN is present. injectErrorTracking
// (src/apps/web/seo/inject.ts) writes window.__SENTRY_DSN__ from SENTRY_DSN at
// HTML serve time, so dev and any env without the secret stay silent.
if (typeof window !== "undefined" && window.__SENTRY_DSN__) {
  Sentry.init({
    dsn: window.__SENTRY_DSN__,
    tracesSampleRate: 0.1,
    replaysOnErrorSampleRate: 0.5,
    replaysSessionSampleRate: 0,
    environment: window.location.hostname.includes("localhost")
      ? "development"
      : "production",
  });
}

const FallbackError = (
  <div className="min-h-screen flex items-center justify-center bg-paper text-foreground p-8">
    <div className="max-w-md text-center space-y-4">
      <div className="font-mono text-xs text-signal-red tracking-wider uppercase">
        ● Something snapped
      </div>
      <h1 className="font-serif text-2xl">
        We just told the on-call. A refresh usually un-snaps it.
      </h1>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="font-mono text-xs underline underline-offset-4"
      >
        ↻ Reload
      </button>
    </div>
  </div>
);

function start() {
  const root = createRoot(document.getElementById("root")!);
  root.render(
    <Sentry.ErrorBoundary fallback={FallbackError}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Sentry.ErrorBoundary>,
  );
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}
